import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useCustomParamId } from 'hooks/useParamId';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect } from 'react';
import { PiCaretLeft, PiShieldPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useLoginServiceCode } from 'services/auth/serviceCode';
import { ILoginServiceCode } from 'services/auth/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import * as Yup from 'yup';

const ServiceLogin = () => {
  const token = useCustomParamId('token', 'login');

  const { serviceTokenExpiresAt } = useStoreState();

  const navigate = useNavigate();

  const loginServiceCode = useLoginServiceCode();

  const mutateOptions = useMutateOptions<ILoginServiceCode>();

  const onGoBackToLogin = () => navigate(`/login`, { replace: true });

  const onLoginTwoFactor = async (
    body: ILoginServiceCode,
    helpers: FormikHelpers<ILoginServiceCode>
  ) => {
    await loginServiceCode.mutateAsync(body, helpers && mutateOptions(helpers));
  };

  useEffect(() => {
    if (!token || !serviceTokenExpiresAt) {
      onGoBackToLogin();
    }
    if (token && serviceTokenExpiresAt) {
      const timeout = serviceTokenExpiresAt - 60000 - Date.now();

      console.log(
        `Two Factor Token expires in: ${Math.round(timeout / 60000)} min`
      );

      const timerId = setTimeout(() => onGoBackToLogin(), timeout);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [token]);

  const initialValues: ILoginServiceCode = {
    serviceCode: '',
    token: token || '',
  };

  const validationSchema = Yup.object().shape({
    serviceCode: Yup.string().required(i18next.t('formik-error.required')),
    token: Yup.string(),
  });

  return (
    <PublicPage
      header={
        <>
          <PiShieldPlus />
          Enter the service code
        </>
      }
      footer={
        <div
          className='flex gap-2 cursor-pointer p-4 items-center'
          onClick={onGoBackToLogin}>
          <PiCaretLeft />
          Back
        </div>
      }>
      <Formik
        validateOnBlur
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onLoginTwoFactor}>
        {({}) => (
          <Form>
            <FormInput.Text
              name='serviceCode'
              type='text'
              label={'Service code'}
            />
            <Button
              isLoading={loginServiceCode.isPending}
              type='submit'
              variant={BUTTON_VARIANT.submit}
              fullWidth>
              <Button.Text text={'Login via service code'} />
            </Button>
          </Form>
        )}
      </Formik>
    </PublicPage>
  );
};

export default ServiceLogin;
